import type { AutomationInputsType, NodeContextType } from '@unifyapps/defs/types/dataSource';
import _omit from 'lodash/omit';
import _pick from 'lodash/pick';
import { memo, useEffect } from 'react';
import isEqual from 'react-fast-compare';
import DataSourceHelper from '../../../../helper/DataSourceHelper';
import { InterfaceModes } from '../../../../stores/InterfaceStore';
import { KEYS_TO_MATCH } from '../../consts';
import { useDSCInterfaceRecord, useDSCIsPublicPage, useDSCMode } from '../../context/selectors';
import { useBuildTransformerParameters } from '../../hooks/useBuildTransformerParameters';
import { useComputeDataSourcePayload } from '../../hooks/useComputeDataSourcePayload';
import { useExecuteDataSourceCallbacks } from '../../hooks/useExecuteDataSourceCallbacks';
import { useGenerateQueryOptionsFromAdvancedOptions } from '../../hooks/useGenerateQueryOptionsFromAdvancedOptions';
import { useGetPathToOmitFromQueryKey } from '../../hooks/useGetPathToOmitFromQueryKey';
import { useNodeOptionsBuilder } from '../../hooks/useNodeOptionsBuilder';
import type { DataSourceWorkflowNodeProps } from '../../types';
import { getEffectiveIsPublicPage } from '../../utils';
import { DataSourcePulseObserverGate } from '../DataSourcePulseObserver/DataSourcePulseObserverGate';
import { useAddSystemParameters } from './hooks/useAddSystemParameters';
import useDataSourceWorkflowNode from './hooks/useDataSourceWorkflowNode';

function DataSourceWorkflowNode({
  id,
  originalDataSourceId,
  requestId,
  inputs,
  options,
  context,
  lookupRequests,
  onQueryCompletion,
  name,
  callbacks,
  advancedOptions,
  setDataSourceState,
  getCurrentQueryResult,
  refetchOnPayloadChange,
  disabled,
  transformerCode,
  dependsOn,
  dynamicPaths,
  defaultRetryConfig,
  version,
}: DataSourceWorkflowNodeProps) {
  const mode = useDSCMode();
  const interfaceRecord = useDSCInterfaceRecord();
  const isPublicPage = useDSCIsPublicPage();
  const isBuilderMode = mode === InterfaceModes.BUILDER;
  const effectiveIsPublicPage = getEffectiveIsPublicPage(mode, isPublicPage);
  const isTransformerConfigured = DataSourceHelper.isTransformerConfigured(transformerCode);
  const computedPayload = useComputeDataSourcePayload<{
    inputs: AutomationInputsType;
    context?: NodeContextType;
  }>({
    data: { inputs, context },
    refetchOnPayloadChange,
    dataSourceId: id,
    key: requestId,
    dependsOn,
    dynamicPaths,
  });

  const decoratedInputs = useAddSystemParameters(computedPayload);
  const pathToOmitFromQueryKey = useGetPathToOmitFromQueryKey({
    dataSourceId: id,
    dynamicPaths,
    refetchOnPayloadChange,
    inputs,
  });
  const queryOptions = useGenerateQueryOptionsFromAdvancedOptions({
    advancedOptions,
    defaultRetryConfig,
  });
  const nodeOptions = useNodeOptionsBuilder({ options });
  const transformerParameters = useBuildTransformerParameters({ transformerCode });

  const queryResult = useDataSourceWorkflowNode({
    payload: {
      context: computedPayload.context,
      id: originalDataSourceId,
      inputs: decoratedInputs,
      lookupRequests,
      options: nodeOptions,
      transformerParameters,
      // version, @anandabhi04 fix this later, BE reverted this change
    },
    enabled: !disabled,
    debugParams: {
      name,
      requestId,
      meta: {
        isPublicPage: effectiveIsPublicPage,
      },
    },
    queryOptions,
    options: {
      useDirectApi: advancedOptions?.useDirectApi,
      isTransformerConfigured,
      isBuilderMode,
      pathToOmitFromQueryKey,
    },
    interfaceRecord,
  });

  useEffect(() => {
    const currentQueryResult = getCurrentQueryResult({
      id,
      requestId,
    });

    if (isEqual(_pick(queryResult, KEYS_TO_MATCH), _pick(currentQueryResult, KEYS_TO_MATCH))) {
      return;
    }

    setDataSourceState({
      id,
      result: _omit(queryResult, 'outputSchema'),
      requestId,
    });
  }, [getCurrentQueryResult, queryResult, id, setDataSourceState, requestId]);

  useExecuteDataSourceCallbacks({
    callbacks,
    onQueryCompletion,
    requestId,
    id,
    outputSchema: queryResult.outputSchema,
    queryResult: {
      error: queryResult.error,
      isLoading: queryResult.isLoading,
      isFetching: queryResult.isFetching,
    },
    disabled,
  });

  return (
    <DataSourcePulseObserverGate
      automationId={queryResult.automationId}
      dsId={id}
      enabled={!disabled}
      error={queryResult.error}
      executionInstanceId={queryResult.executionInstanceId}
      isFetching={Boolean(queryResult.isFetching)}
      objects={queryResult.objects}
      requestPayload={decoratedInputs}
    />
  );
}

export default memo(DataSourceWorkflowNode);
